import React, { useEffect, useState } from 'react'
import {
	Card,
	message,
	Button,
	Space,
	Modal,
	Form,
	Input,
	Select,
	Typography,
	Popconfirm,
} from 'antd'
import {
	UserAddOutlined,
	EditOutlined,
	DeleteOutlined,
	ExclamationCircleFilled,
	CopyOutlined,
	DownloadOutlined,
	TeamOutlined,
} from '@ant-design/icons'
import GlobalHeader from '../../common/components/Header/GlobalHeader'
import PageHeader from '../../common/components/Header/PageHeader'
import { AntTable } from '../../common/components/Table/AntTable'
import ReactBeatLoader from '../../common/components/Spinner/ReactBeatLoader'
import { userService } from '../../common/service/user.service'
import { staffService } from '../../common/service/staff.service'
import { departmentService } from '../../common/service/department.service'
import ViewStaffDetail from '../Staff/StaffDetail'
import StaffCreateUpdate from '../Staff/StaffCreateUpdate'
import { useTheme, useThemeColors } from '../../common/ThemeContext'

interface UserData {
	uid?: string
	email?: string
	fullName?: string
	role?: string
	staffId?: string | null
	departmentIds?: string[]
}

interface CreateUserResponse {
	email: string
	role: string
	id: string
	temporaryPassword: string
	message: string
	fullName?: string
	staffId?: string | null
	departmentIds?: string[]
}

function User() {
	const { isDarkMode } = useTheme()
	const { primary } = useThemeColors()
	const [adminUsers, setAdminUsers] = useState<UserData[]>([])
	const [loading, setLoading] = useState(false)
	const [isModalVisible, setIsModalVisible] = useState(false)
	const [viewStaffId, setViewStaffId] = useState<string | null>(null)
	const [editStaffId, setEditStaffId] = useState<string | null>(null)
	const [editingUser, setEditingUser] = useState<UserData | null>(null)
	const [staffOptions, setStaffOptions] = useState<Array<{ value: string; label: string }>>([])
	const [departmentOptions, setDepartmentOptions] = useState<
		Array<{ value: string; label: string }>
	>([])
	const [form] = Form.useForm()

	useEffect(() => {
		fetchUsers()
		fetchDepartments()
	}, [])

	const fetchDepartments = async () => {
		try {
			const res: any = await departmentService.fetchListing()
			const items = res?.data ?? []
			if (Array.isArray(items)) {
				setDepartmentOptions(
					items.map((d: any) => ({
						value: d.id,
						label: d.name || d.id,
					})),
				)
			}
		} catch (error) {
			console.error('Failed to fetch departments:', error)
		}
	}

	const searchStaff = async (q: string) => {
		try {
			const keyword = q.trim()
			if (!keyword) {
				setStaffOptions([])
				return
			}
			const items = await staffService.autocomplete(keyword)
			setStaffOptions(items.map((s) => ({ value: s.id, label: `${s.name} (${s.id})` })))
		} catch (error) {
			console.error('Failed to search staff:', error)
		}
	}

	const fetchUsers = async () => {
		try {
			setLoading(true)
			const response: any = await userService.fetchListing()
			if (response?.statusCode === 200 && response?.data) {
				setAdminUsers(response.data)
			}
		} catch (error: any) {
			console.error('Failed to fetch users:', error)
			message.error('Failed to load users')
		} finally {
			setLoading(false)
		}
	}

	const handleCreate = () => {
		setEditingUser(null)
		form.resetFields()
		setIsModalVisible(true)
	}

	const handleEdit = (user: UserData) => {
		setEditingUser(user)
		form.setFieldsValue({
			email: user.email,
			fullName: user.fullName,
			role: user.role,
			staffId: user.staffId || undefined,
			departmentIds: user.departmentIds || [],
		})
		if (user.staffId) {
			setStaffOptions([{ value: user.staffId, label: user.staffId }])
		}
		setIsModalVisible(true)
	}

	const downloadUserInfo = (userData: CreateUserResponse) => {
		const content = `User Information
===================

Email: ${userData.email}
Full Name: ${userData.fullName || 'N/A'}
User ID: ${userData.id}
Role: ${userData.role}
Linked Staff ID: ${userData.staffId || 'N/A'}
Department Scope: ${userData.departmentIds?.join(', ') || 'All'}
Temporary Password: ${userData.temporaryPassword}

Date Created: ${new Date().toLocaleString()}

===================
IMPORTANT: Please keep this information secure and share it only with the intended user.
The user should change this temporary password after their first login.
`

		const blob = new Blob([content], { type: 'text/plain' })
		const url = window.URL.createObjectURL(blob)
		const link = document.createElement('a')
		link.href = url
		link.download = `user_${userData.email.replace('@', '_at_')}_${Date.now()}.txt`
		link.click()
		window.URL.revokeObjectURL(url)
		message.success('User information downloaded successfully')
	}

	const handleDelete = async (userId: string) => {
		try {
			await userService.delete(userId)
			message.success('User deleted successfully')
			fetchUsers()
		} catch (error: any) {
			console.error('Failed to delete user:', error)
			message.error(error?.response?.data?.message || 'Failed to delete user')
		}
	}

	const handleModalOk = async () => {
		try {
			const values = await form.validateFields()

			if (editingUser) {
				// Update existing user
				await userService.update(editingUser.uid!, values)
				message.success('User updated successfully')
			} else {
				// Create new user
				const response: any = await userService.create(values)
				if (response?.data) {
					const userData = response.data as CreateUserResponse
					// Add fullName to userData if not present
					const userDataWithFullName = {
						...userData,
						fullName: values.fullName,
					}
					// Show temporary password in a modal
					Modal.success({
						title: 'User Created Successfully',
						width: 600,
						content: (
							<div>
								<p>
									<strong>Email:</strong> {userData.email}
								</p>
								<p>
									<strong>Full Name:</strong> {values.fullName}
								</p>
								<p>
									<strong>Role:</strong> {userData.role}
								</p>
								<p>
									<strong>Temporary Password:</strong>
								</p>
								<div
									style={{
										display: 'flex',
										alignItems: 'center',
										gap: '8px',
										marginTop: '8px',
									}}
								>
									<Input.Password
										readOnly
										value={userData.temporaryPassword}
										style={{ flex: 1 }}
									/>
									<Button
										icon={<CopyOutlined />}
										onClick={() => {
											navigator.clipboard.writeText(
												userData.temporaryPassword,
											)
											message.success('Password copied to clipboard')
										}}
									>
										Copy
									</Button>
									<Button
										type='primary'
										icon={<DownloadOutlined />}
										onClick={() => downloadUserInfo(userDataWithFullName)}
									>
										Download
									</Button>
								</div>
								<p style={{ marginTop: '16px', color: '#ff4d4f' }}>
									<strong>Important:</strong> Please save this password and share
									it securely with the user. This password cannot be retrieved
									again.
								</p>
							</div>
						),
					})
				}
			}

			setIsModalVisible(false)
			form.resetFields()
			fetchUsers()
		} catch (error: any) {
			console.error('Failed to save user:', error)
			message.error(error?.response?.data?.message || 'Failed to save user')
		}
	}

	const handleModalCancel = () => {
		setIsModalVisible(false)
		form.resetFields()
	}

	const closeViewStaffModal = () => {
		setViewStaffId(null)
	}

	const closeEditStaffModal = () => {
		setEditStaffId(null)
	}

	const showDeleteConfirm = (user: UserData) => {
		Modal.confirm({
			title: 'Are you sure you want to delete this user?',
			icon: <ExclamationCircleFilled />,
			content: (
				<div>
					<p>
						<strong>Email:</strong> {user.email}
					</p>
					<p>
						<strong>Full Name:</strong> {user.fullName}
					</p>
					<p style={{ color: '#ff4d4f' }}>This action cannot be undone.</p>
				</div>
			),
			okText: 'Delete',
			okType: 'danger',
			cancelText: 'Cancel',
			onOk() {
				handleDelete(user.uid!)
			},
		})
	}

	// Define columns for AntTable
	const columns = [
		{
			title: 'User ID',
			dataIndex: 'uid',
			key: 'uid',
		},
		{
			title: 'Email',
			dataIndex: 'email',
			key: 'email',
		},
		{
			title: 'Full Name',
			dataIndex: 'fullName',
			key: 'fullName',
		},
		{
			title: 'Role',
			dataIndex: 'role',
			key: 'role',
		},
		{
			title: 'Linked Staff ID',
			dataIndex: 'staffId',
			key: 'staffId',
			render: (value: string | null | undefined) => {
				if (!value) return '-'
				return (
					<Button
						type='link'
						size='small'
						style={{ padding: 0, height: 'auto' }}
						onClick={() => setViewStaffId(value)}
					>
						{value}
					</Button>
				)
			},
		},
		{
			title: 'Department Scope',
			dataIndex: 'departmentIds',
			key: 'departmentIds',
			render: (value: string[] | undefined) =>
				value && value.length > 0 ? value.join(', ') : 'All Departments',
		},
		{
			title: 'Actions',
			key: 'actions',
			render: (_: any, record: UserData) => (
				<Space size='small'>
					<Button
						size='small'
						type='link'
						icon={<EditOutlined />}
						onClick={() => handleEdit(record)}
					>
						Edit
					</Button>
					<Button
						size='small'
						type='link'
						danger
						icon={<DeleteOutlined />}
						onClick={() => showDeleteConfirm(record)}
					>
						Delete
					</Button>
				</Space>
			),
		},
	]

	return (
		<>
			<GlobalHeader />

			<PageHeader
				title='System Users'
				subtitle='Manage administrator accounts'
				icon={<TeamOutlined />}
				extra={
					<Button
						type='primary'
						ghost={isDarkMode}
						icon={<UserAddOutlined />}
						onClick={handleCreate}
						style={isDarkMode ? {
							backgroundColor: 'rgba(56, 189, 248, 0.08)',
							fontWeight: 600,
						} : {}}
					>
						Add New User
					</Button>
				}
			/>

			{/* ── Table ───────────────────────────────────────────────────── */}
			<div style={{ padding: '8px 24px 24px' }}>
				<Card
					style={{ borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}
					styles={{ body: { padding: 0 } }}
				>
					{loading ? (
						<div style={{ padding: 24 }}>
							<ReactBeatLoader />
						</div>
					) : (
						<AntTable columns={columns} dataSource={adminUsers} rowKey='uid' />
					)}
				</Card>
			</div>

			<Modal
				title={editingUser ? 'Edit User' : 'Create New User'}
				open={isModalVisible}
				onOk={handleModalOk}
				onCancel={handleModalCancel}
				okText={editingUser ? 'Update' : 'Create'}
				width={600}
			>
				<Form form={form} layout='vertical'>
					<Form.Item
						label='Email'
						name='email'
						rules={[
							{ required: true, message: 'Please input the email!' },
							{ type: 'email', message: 'Please enter a valid email!' },
						]}
					>
						<Input placeholder='user@example.com' disabled={!!editingUser} />
					</Form.Item>

					<Form.Item
						label='Full Name'
						name='fullName'
						rules={[{ required: true, message: 'Please input the full name!' }]}
					>
						<Input placeholder='John Doe' />
					</Form.Item>

					<Form.Item
						label='Role'
						name='role'
						rules={[{ required: true, message: 'Please select a role!' }]}
						initialValue='admin'
					>
						<Select placeholder='Select a role'>
							<Select.Option value='admin'>admin</Select.Option>
							{/* <Select.Option value='user'>User</Select.Option>
							<Select.Option value='superadmin'>Super Admin</Select.Option> */}
						</Select>
					</Form.Item>

					<Form.Item label='Linked Staff (for mobile admin approval)' name='staffId'>
						<Select
							allowClear
							showSearch
							filterOption={false}
							onSearch={searchStaff}
							options={staffOptions}
							placeholder='Search staff by name'
						/>
					</Form.Item>

					<Form.Item
						label='Department Scope'
						name='departmentIds'
						help='Empty means this admin can approve all departments.'
					>
						<Select
							mode='multiple'
							allowClear
							options={departmentOptions}
							placeholder='Select responsible departments'
						/>
					</Form.Item>

					{!editingUser && (
						<div
							style={{
								padding: '12px',
								backgroundColor: '#e6f7ff',
								border: '1px solid #91d5ff',
								borderRadius: '4px',
								marginTop: '16px',
							}}
						>
							<p style={{ margin: 0, fontSize: '12px', color: '#0050b3' }}>
								<strong>Note:</strong> A temporary password will be generated
								automatically and shown after user creation. Please save it and
								share it securely with the user.
							</p>
						</div>
					)}
				</Form>
			</Modal>

			<Modal
				title={null}
				open={!!viewStaffId}
				onCancel={closeViewStaffModal}
				footer={null}
				destroyOnHidden
				width={1200}
			>
				{viewStaffId && (
					<ViewStaffDetail
						staffId={viewStaffId}
						embedded
						onClose={closeViewStaffModal}
						onEdit={(id) => {
							setViewStaffId(null)
							setEditStaffId(id)
						}}
					/>
				)}
			</Modal>

			<Modal
				title={null}
				open={!!editStaffId}
				onCancel={closeEditStaffModal}
				footer={null}
				destroyOnHidden
				width={1200}
			>
				{editStaffId && (
					<StaffCreateUpdate
						staffIdOverride={editStaffId}
						embedded
						onClose={closeEditStaffModal}
						onSuccess={() => {
							fetchUsers()
						}}
					/>
				)}
			</Modal>
		</>
	)
}

export default User
